import { Box } from "@mui/material";
import { Skeleton, Typography } from "@trimble/modus-cmx";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MenuHeader from "@/components/ui/Menu/MenuHeader";
import OverflowMenu, { type OverflowMenuRef } from "@/components/OverflowMenu";
import { useRecentlyViewedItems } from "@/hooks/useRecentlyViewedItems";
import RecentlyViewedListItem from "./RecentlyViewedListItem";
import type { RecentlyViewedItem } from "./types";

const RecentlyViewedLoader = ({ ariaLabel }: { ariaLabel: string }) => {
  return (
    <Box aria-busy="true" aria-label={ariaLabel}>
      {Array.from({ length: 3 }, (_, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            minHeight: 100,
            px: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Skeleton
            shape="rectangle"
            height="0.5rem"
            width={`${14 - index * 2}rem`}
            style={{ display: "block" }}
          />
        </Box>
      ))}
    </Box>
  );
};

type RecentlyViewedBodyProps = {
  items: RecentlyViewedItem[];
  loading: boolean;
  onSelect: () => void;
};

const RecentlyViewedBody = ({
  items,
  loading,
  onSelect,
}: RecentlyViewedBodyProps) => {
  const { t } = useTranslation();

  if (loading) {
    return <RecentlyViewedLoader ariaLabel={t("loading")} />;
  }

  if (items.length === 0) {
    return (
      <div style={{ padding: "24px 16px" }}>
        <Typography
          hierarchy="p"
          size="sm"
          style={{ color: "var(--modus-wc-color-gray-6)" }}
        >
          <span>{t("global_settings.recently_viewed.empty")}</span>
        </Typography>
      </div>
    );
  }

  return items.map((item) => (
    <RecentlyViewedListItem key={item.id} item={item} onSelect={onSelect} />
  ));
};

function RecentlyViewed() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { items, loading } = useRecentlyViewedItems({ skip: !isOpen });
  const menuRef = useRef<OverflowMenuRef>(null);

  const handleSelect = () => {
    if (menuRef.current) {
      menuRef.current.menuVisible = false;
    }
  };

  return (
    <OverflowMenu
      ref={menuRef}
      buttonAriaLabel={t("global_settings.recently_viewed.title")}
      iconName="history"
      iconStyle={{ color: "var(--modus-wc-color-neutral-content)" }}
      placement="bottom-end"
      offset={[0, 2]}
      requireMenuItems={false}
      onOpenChange={setIsOpen}
    >
      <div
        style={{
          width: 320,
          maxHeight: 480,
          overflowY: "auto",
        }}
      >
        <MenuHeader title={t("global_settings.recently_viewed.menu_title")} />
        <RecentlyViewedBody
          items={items}
          loading={loading}
          onSelect={handleSelect}
        />
      </div>
    </OverflowMenu>
  );
}

export default RecentlyViewed;
